import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { StaffCategory, UserRole } from '../types';

// ─── provisionSchool ──────────────────────────────────────────────────────────

export interface ProvisionSchoolInput {
  schoolName: string;
  adminEmail: string;
  adminDisplayName: string;
  urn?: string;
  localAuthority?: string;
  plan?: 'trial' | 'paid';
  trialEndsAt?: string;   // ISO date string — only used when plan = 'trial'
  licenceCount?: number;  // 0 = unlimited
}
export interface ProvisionSchoolResult {
  schoolId: string;
  adminUid: string;
}
export const provisionSchool = httpsCallable<ProvisionSchoolInput, ProvisionSchoolResult>(
  functions, 'provisionSchool',
);

// ─── createUser ───────────────────────────────────────────────────────────────

export interface CreateUserInput {
  email: string;
  displayName: string;
  role: UserRole;
  staffCategory?: StaffCategory;
  lineManagerId?: string;
  schoolId?: string;
}
export interface CreateUserResult {
  uid: string;
}
export const createUser = httpsCallable<CreateUserInput, CreateUserResult>(
  functions, 'createUser',
);

// ─── deactivateUser ───────────────────────────────────────────────────────────

export interface DeactivateUserInput {
  targetUid: string;
}
export const deactivateUser = httpsCallable<DeactivateUserInput, { success: boolean }>(
  functions, 'deactivateUser',
);
export const reactivateUser = httpsCallable<{ targetUid: string }, { success: boolean }>(
  functions, 'reactivateUser',
);

// ─── listSchools ──────────────────────────────────────────────────────────────

export interface SchoolSummary {
  id: string;
  name: string;
  isActive: boolean;
  urn?: string | null;
  localAuthority?: string | null;
  plan?: string | null;           // 'trial' | 'paid' | 'suspended'
  trialEndsAt?: string | null;    // ISO date string
  adminNotes?: string | null;
  userCount: number;
  licenceCount: number;
  activeStaffCount: number;
  legalAcceptedAt?: string | null;
  legalAcceptedByName?: string | null;
  createdAt?: string | null;
}
export const listSchools = httpsCallable<Record<string, never>, SchoolSummary[]>(
  functions, 'listSchools',
);
export const getSchool = httpsCallable<{ schoolId: string }, SchoolSummary>(
  functions, 'getSchool',
);

// ─── launchCycle ─────────────────────────────────────────────────────────────

export const launchCycle = httpsCallable<{ cycleId: string }, { reviewsCreated: number }>(
  functions, 'launchCycle',
);

// ─── triggerMidYear ───────────────────────────────────────────────────────────

export const triggerMidYear = httpsCallable<{ cycleId: string }, { reviewsOpened: number }>(
  functions, 'triggerMidYear',
);
export const deleteCycle = httpsCallable<{ cycleId: string }, { success: boolean }>(
  functions, 'deleteCycle',
);

// ─── getSchoolUsers ───────────────────────────────────────────────────────────

export interface UserSummary {
  id: string;
  displayName: string;
  email: string;
  role: UserRole;
  staffCategory?: StaffCategory;
  lineManagerId?: string;
  isFormTutor?: boolean;
  isPastoralLeader?: boolean;
  isAcademicLeader?: boolean;
  isSLT?: boolean;
  isAtTopOfPaySpine?: boolean;
  isActive: boolean;
  lastSignIn?: string | null;
  createdAt?: string | null;
}
export const getSchoolUsers = httpsCallable<{ schoolId: string }, UserSummary[]>(
  functions, 'getSchoolUsers',
);

export const renameSchool = httpsCallable<{ schoolId: string; name: string }, { success: boolean }>(
  functions, 'renameSchool',
);

export const deactivateSchool = httpsCallable<{ schoolId: string; isActive: boolean }, { success: boolean }>(
  functions, 'deactivateSchool',
);

// ─── getSchoolStats ───────────────────────────────────────────────────────────

export interface SchoolStats {
  totalCycles: number;
  activeCycleName: string | null;
  totalReviews: number;
}
export const getSchoolStats = httpsCallable<{ schoolId: string }, SchoolStats>(
  functions, 'getSchoolStats',
);

// ─── updateUserAdmin ──────────────────────────────────────────────────────────

export const updateUserAdmin = httpsCallable<
  { targetUid: string; role?: string; staffCategory?: string; lineManagerId?: string },
  { success: boolean }
>(functions, 'updateUserAdmin');

// ─── getSchoolCycles ──────────────────────────────────────────────────────────

export interface CycleSummary {
  id: string;
  name: string;
  status: string;
  staffCategory: string;
  startDate: string | null;
  staffDeadline: string | null;
  lineManagerDeadline: string | null;
  reviewCount: number;
  createdAt: string | null;
}
export const getSchoolCycles = httpsCallable<{ schoolId: string }, CycleSummary[]>(
  functions, 'getSchoolCycles',
);

// ─── getCycleReviewStats ──────────────────────────────────────────────────────

export interface CycleReviewStats {
  cycleName: string;
  status: string;
  staffCategory: string;
  startDate: string | null;
  staffDeadline: string | null;
  lineManagerDeadline: string | null;
  totalReviews: number;
  byStatus: Record<string, number>;
  reviews: { id: string; revieweeName: string; reviewerName: string; status: string }[];
}
export const getCycleReviewStats = httpsCallable<
  { cycleId: string; schoolId: string },
  CycleReviewStats
>(functions, 'getCycleReviewStats');

// ─── bulkCreateUsers ──────────────────────────────────────────────────────────

export interface BulkCreateResult {
  created: number;
  failed: { email: string; error: string }[];
}
export const bulkCreateUsers = httpsCallable<
  { schoolId: string; users: { email: string; displayName: string; role: string; staffCategory?: string }[] },
  BulkCreateResult
>(functions, 'bulkCreateUsers');

// ─── setSchoolPlan ────────────────────────────────────────────────────────────

export interface SetSchoolPlanInput {
  schoolId: string;
  plan?: 'trial' | 'paid' | 'suspended';
  trialEndsAt?: string | null;
  adminNotes?: string | null;
}
export const setSchoolPlan = httpsCallable<SetSchoolPlanInput, { success: boolean }>(
  functions, 'setSchoolPlan',
);

// ─── setLicenceCount ──────────────────────────────────────────────────────────

export const setLicenceCount = httpsCallable<
  { schoolId: string; licenceCount: number },
  { success: boolean }
>(functions, 'setLicenceCount');

// ─── acceptLegalTerms ─────────────────────────────────────────────────────────

export const acceptLegalTerms = httpsCallable<
  { schoolId: string; version: string },
  { success: boolean }
>(functions, 'acceptLegalTerms');

// ─── enrollStaffInCycle ───────────────────────────────────────────────────────

export interface EnrollStaffInput {
  staffUid: string;
  cycleId: string;
  roleFlagsOverride?: {
    isFormTutor: boolean;
    isPastoralLeader: boolean;
    isAcademicLeader: boolean;
    isSLT: boolean;
    isAtTopOfPaySpine: boolean;
  };
}
export const enrollStaffInCycle = httpsCallable<EnrollStaffInput, { reviewId: string }>(
  functions, 'enrollStaffInCycle',
);

// ─── purgeCycleData ───────────────────────────────────────────────────────────

export const purgeCycleData = httpsCallable<
  { cycleId: string },
  { success: boolean; reviewsPurged: number }
>(functions, 'purgeCycleData');
